/**
 * Shopping bag (main-cart) line remove/qty with Budget Pay tier sync.
 * Uses section morph on cart page; reload only as fallback.
 * Prefer cart-items-component.updateQuantity when available (single code path).
 */

import { fetchCartJson, seedCartCache } from '@theme/cart-api';
import { alignCartBpTierFromCart } from '@theme/budget-pay-cart-align';
import {
  appendSectionsToBody,
  refreshCartPageAfterMutationWithFallback,
  reloadCartPageFallback,
} from '@theme/cart-section-refresh';
import {
  computeBpExposureCents,
  DEFAULT_BP_GUEST_LIMIT,
  isBudgetPayCartLine,
  validateEligibility,
} from '@theme/budget-pay-rules';

function showLoader() {
  if (typeof window.showTjcLoader === 'function') window.showTjcLoader();
}

function setLoaderProgress(percent) {
  if (typeof window.setTjcLoaderProgress === 'function') window.setTjcLoaderProgress(percent);
}

function hideLoader() {
  if (typeof window.hideTjcLoader === 'function') window.hideTjcLoader();
}

function getCartItemsComponent() {
  return document.querySelector('cart-items-component.tjc_cart_pg, cart-items-component[data-section-id]');
}

function isCartPageWithMorph() {
  return Boolean(getCartItemsComponent());
}

function parseBooleanOrDefault(value, defaultValue) {
  if (value == null || value === '') return defaultValue;
  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n'].includes(normalized)) return false;
  return defaultValue;
}

function parseNumberOrDefault(value, defaultValue) {
  if (value == null || value === '') return defaultValue;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return defaultValue;
  return parsed;
}

function getBpEligibilityFromBody() {
  const d = document.body?.dataset ?? {};
  const guestLimit = parseNumberOrDefault(d.bpGuestLimit, DEFAULT_BP_GUEST_LIMIT);
  const available = parseBooleanOrDefault(d.bpAvailable, true);
  const creditLimit = parseNumberOrDefault(d.bpCreditLimit, guestLimit);
  return { available, creditLimit };
}

async function validateBpQuantityIncrease(line, quantity, existingCart) {
  const cart = existingCart && Array.isArray(existingCart.items) ? existingCart : await fetchCartJson();
  const targetItem = cart?.items?.[line - 1];
  if (!targetItem || !isBudgetPayCartLine(targetItem)) return cart;

  const currentQty = Number(targetItem.quantity ?? 0);
  if (quantity <= currentQty) return cart;

  const { available, creditLimit } = getBpEligibilityFromBody();
  if (!available) {
    throw new Error('Budget Pay is not available for your account.');
  }

  const otherBpLines = (cart?.items ?? []).filter(
    (item, index) => isBudgetPayCartLine(item) && index !== line - 1,
  );
  const projectedLine = { ...targetItem, quantity };
  const exposure = computeBpExposureCents([...otherBpLines, projectedLine]);
  if (!exposure.ok) {
    throw new Error(exposure.message);
  }

  const validation = validateEligibility({ available, creditLimit }, exposure.exposureCents);
  if (!validation.ok) {
    throw new Error(validation.message);
  }

  return cart;
}

async function changeCartLine(line, quantity) {
  const lineNumber = Number(line);
  const qty = Number(quantity);
  if (!Number.isFinite(lineNumber) || lineNumber < 1) {
    throw new Error('Invalid cart line');
  }
  if (!Number.isFinite(qty) || qty < 0) {
    throw new Error('Invalid quantity');
  }

  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(
      appendSectionsToBody({
        line: lineNumber,
        quantity: qty,
      }),
    ),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.description || data?.message || `Cart update failed (${res.status})`;
    throw new Error(msg);
  }
  seedCartCache(data);
  return data;
}

async function finishCartLineMutation(cartData, action) {
  if (isCartPageWithMorph()) {
    await refreshCartPageAfterMutationWithFallback(cartData, {
      action,
      source: 'main-cart-bp-sync',
      skipFreeGiftSync: true,
    });
    return;
  }
  reloadCartPageFallback();
}

async function changeLineThenAlignBp(line, quantity) {
  let cartData = null;

  if (quantity > 0) {
    setLoaderProgress(18);
    cartData = await validateBpQuantityIncrease(line, quantity);
  }

  setLoaderProgress(35);
  cartData = await changeCartLine(line, quantity);
  setLoaderProgress(55);

  try {
    const { cart: alignedCart, didMutate } = await alignCartBpTierFromCart(cartData);
    if (didMutate) cartData = alignedCart;
  } catch (err) {
    console.error('[BudgetPay] cart tier align after update', err);
  }

  setLoaderProgress(72);
  await finishCartLineMutation(cartData, quantity === 0 ? 'remove' : 'update');
  hideLoader();
}

function delegateToCartItemsComponent(line, quantity) {
  const component = getCartItemsComponent();
  if (!component || typeof component.updateQuantity !== 'function') return false;

  component.updateQuantity({
    line,
    quantity,
    action: quantity === 0 ? 'clear' : 'change',
  });
  return true;
}

/**
 * @param {number} line 1-based cart line index (Liquid item.index + 1)
 */
function removeItem(line) {
  if (delegateToCartItemsComponent(line, 0)) return;

  showLoader();
  changeLineThenAlignBp(line, 0).catch((err) => {
    hideLoader();
    const message = err instanceof Error ? err.message : 'Could not update cart.';
    window.alert(message);
    console.error('Error:', err);
  });
}

/**
 * @param {number} line 1-based cart line index
 * @param {string|number} quantity
 */
function updateCartItem(line, quantity) {
  const qty = parseInt(String(quantity), 10);
  if (!Number.isFinite(qty) || qty < 0) return;

  if (delegateToCartItemsComponent(line, qty)) return;

  showLoader();
  changeLineThenAlignBp(line, qty).catch((err) => {
    hideLoader();
    const message = err instanceof Error ? err.message : 'Could not update cart.';
    window.alert(message);
    console.error('Error:', err);
  });
}

/**
 * @param {string} key Cart line item key
 */
async function removeItemByKey(key) {
  showLoader();
  try {
    setLoaderProgress(20);
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(
        appendSectionsToBody({
          updates: { [key]: 0 },
        }),
      ),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = data?.description || data?.message || `Cart update failed (${res.status})`;
      throw new Error(msg);
    }
    setLoaderProgress(50);
    let cartData = data;
    try {
      const { cart: alignedCart, didMutate } = await alignCartBpTierFromCart(cartData);
      if (didMutate) cartData = alignedCart;
    } catch (err) {
      console.error('[BudgetPay] cart tier align after key-remove', err);
    }
    setLoaderProgress(72);
    await finishCartLineMutation(cartData, 'remove');
    hideLoader();
  } catch (err) {
    hideLoader();
    console.error('[Cart] removeItemByKey error:', err);
    if (isCartPageWithMorph()) {
      reloadCartPageFallback();
    }
  }
}

window.removeItem = removeItem;
window.removeItemByKey = removeItemByKey;
window.updateCartItem = updateCartItem;